import moment from 'moment'
import CryptoJS from 'crypto-js'

import { CONFIG } from 'Config/config'
import { urlAddExist, urlRemove } from 'Utils/URL'
import { exportHeaders } from './options'
import { escapeCsvString } from '../../../../../shared/csv'
import { downloadStringAsFile } from '../../../../../Utils/Misc'
import {
  CAMPAIGN_INTERNAL_STATUSES,
  CAMPAIGN_ENUM_TYPES,
  CAMPAIGN_SUBTYPES_ENUM,
  CAMPAIGN_DASHBOARD_BY_CAMPAIGN_TYPE,
} from '../../../../../shared/constants'
import { authorization } from '../../../../../Module/Auth/Auth'
import { apiClient } from '../../../../../Module/API/Client'

/**
 * Get new object with filtered pipelines
 * @param {object} pipelines unfiltered
 * @param {function} func filter
 * @return {object} pipelines filtered
 */

const filterPipelines = (pipelines, func) =>
  Object.entries(pipelines).reduce(
    (acc, [key, array]) => {
      acc[key] = array.filter(func)
      return acc
    },
    {
      [CAMPAIGN_INTERNAL_STATUSES.DRAFT]: [],
      [CAMPAIGN_INTERNAL_STATUSES.IN_REVIEW]: [],
      [CAMPAIGN_INTERNAL_STATUSES.CODE_REVIEW]: [],
      [CAMPAIGN_INTERNAL_STATUSES.GENERATING_DIGITAL_PROOF]: [],
      [CAMPAIGN_INTERNAL_STATUSES.PAYMENT]: [],
      [CAMPAIGN_INTERNAL_STATUSES.ACTIVE]: [],
    },
  )

const formatDate = date => {
  // Coerce the UTC saved date to user's tz
  return date
    ? moment(
        moment(Number(date))
          .utc()
          .format()
          .split('T')[0],
        'YYYY-MM-DD',
        true,
      ).format('DD MMM YYYY')
    : '-'
}

const formatExportData = data => ({
  'Id': data.campaignId,
  'Client name': data.clientName,
  'Campaign name': data.campaignName,
  'Amount': data.totalCosts,
  'Submitted': data.submitDate,
  'Launch date': data.startDate,
  'Cities': data.cityName,
  'Missions': data.missionsCount,
  'Missions per week': Math.ceil(data.missionsCount / data.campaignDuration),
  'Weeks': data.campaignDuration,
  'Stage': data.internalStatus,
  'Stage reached date': data.internalStatusChangedDate,
})

const openLinkInNewTab = url => {
  const newWindow = window.open(url, '_blank', 'noopener,noreferrer')
  if (newWindow) newWindow.opener = null
}

const openLinkInSameTab = url => {
  const newWindow = (window.location.href = url)
  if (newWindow) newWindow.opener = null
}

const onExportData = (pipelines, countryName) => {
  let content = exportHeaders.join(',')

  const rows = Object.values(pipelines).flat()
  rows.forEach(item => {
    const rowData = formatExportData({ ...item })

    content +=
      '\n' +
      exportHeaders
        .map(headerName => {
          const value = rowData[headerName]
          return typeof value === 'string' ? escapeCsvString(value) : value
        })
        .join(',')
  })
  downloadStringAsFile(content, `campaigns_${countryName}.csv`, 'text/csv')
}

const getCampaignBuilderPageUrl = ({
  clientId,
  campaignId,
  lastActiveStep,
  channel,
  subtype,
}) => {
  if (!authorization.isLogged()) {
    // eslint-disable-next-line no-console
    console.log(
      '## debug, getCampaignBuilderPageUrl, redirect to /errors?type=auth',
    )
    window.location.href = '/errors?type=auth'
  }

  const isADM =
    channel === CAMPAIGN_ENUM_TYPES.DIRECTMAIL &&
    subtype === CAMPAIGN_SUBTYPES_ENUM.ADM

  if (isADM) {
    return `${
      CONFIG.SS_BASE_URL
    }/client/${clientId}/adm/campaign/${campaignId}/builder`
  }

  const isEDDM =
    channel === CAMPAIGN_ENUM_TYPES.DIRECTMAIL &&
    subtype === CAMPAIGN_SUBTYPES_ENUM.EDDM
  if (lastActiveStep === 'design') {
    if (isEDDM) {
      return `${
        CONFIG.SS_BASE_URL
      }/client/${clientId}/eddm/flyer/${campaignId}/upload`
    } else {
      return `${CONFIG.SS_BASE_URL}/client/${clientId}/flyer/${campaignId}`
    }
  } else if (lastActiveStep === 'audience' && isEDDM) {
    return `${CONFIG.SS_BASE_URL}/client/${clientId}/${
      isEDDM ? 'eddm/' : ''
    }campaign${campaignId ? `/${campaignId}` : ''}/segments` // TODO: REMOVE THIS WHEN EDDM CAMPAIGN CREATION CHANGE
  } else if (lastActiveStep === 'segments' && isEDDM) {
    return `${CONFIG.SS_BASE_URL}/client/${clientId}/${
      isEDDM ? 'eddm/' : ''
    }campaign${campaignId ? `/${campaignId}` : ''}/segments` // TODO: REMOVE THIS WHEN EDDM CAMPAIGN CREATION CHANGE
  } else if (lastActiveStep === 'home' && isEDDM) {
    return `${CONFIG.SS_BASE_URL}/client/${clientId}/${
      isEDDM ? 'eddm/' : ''
    }campaign${campaignId ? `/${campaignId}` : ''}/segments`
  } else if (lastActiveStep === 'home' && !isEDDM) {
    return `${CONFIG.SS_BASE_URL}/client/${clientId}/campaign${
      campaignId ? `/${campaignId}` : ''
    }/audience`
  }
  return `${CONFIG.SS_BASE_URL}/client/${clientId}/${
    isEDDM ? 'eddm/' : ''
  }campaign${campaignId ? `/${campaignId}` : ''}${
    lastActiveStep ? `/${lastActiveStep}` : ''
  }`
}

const getDashboardPageUrl = (campaignId, origin, type, subtype) => {
  return `${origin}/dashboard/campaigns/${campaignId}/dashboard/${
    CAMPAIGN_DASHBOARD_BY_CAMPAIGN_TYPE[subtype || type]
  }`
}

const getClientDashboardPageUrl = (campaignId, origin) =>
  `${origin}/dashboard/client-campaigns/${campaignId}/campaign`

const getDigitalProofPageUrl = (campaignId, origin) =>
  `${origin}/dashboard/campaigns/${campaignId}/internal-files`

const getCodesPageUrl = (campaignId, origin) =>
  `${origin}/dashboard/campaigns/${campaignId}/codes`

const splitEmails = emails => emails.split(', ').map(email => email.trim())

const convertCityNameToValue = name => name.toUpperCase().replace(' ', /_/g)

const isHasCity = (campaign, cityFilter) => {
  return (
    campaign.cityName &&
    convertCityNameToValue(campaign.cityName) === cityFilter
  )
}

const isHasSearch = (campaignName, searchValue) =>
  campaignName.toLowerCase().includes(searchValue.toLowerCase())

const encrypt = str =>
  CryptoJS.AES.encrypt(str, CONFIG.CRYPTO_SECRET_KEY).toString()

const addAuthParamsToCampaignBuilderUrl = (campaignBuilderUrl, clientId) => {
  const apiKey = authorization.getAPIKey()
  const user = authorization.getUser()
  const userString = JSON.stringify({
    id: user.id,
    clientId: clientId,
    clientName: user.client ? user.client.name : null,
    firstName: user.firstName,
    lastName: user.lastName,
    phone: user.mobilePhone,
    email: user.email,
    role: user.role,
  })

  const url = new URL(campaignBuilderUrl)

  const cryptedApiKey = encrypt(apiKey)
  const cryptedUser = encrypt(userString)

  url.searchParams.append('key', cryptedApiKey)
  url.searchParams.append('user', cryptedUser)

  return url
}

const getCurrencyCode = country => {
  switch (country.currencySign) {
    case '$':
      if (country.name === 'Canada') {
        return 'CAD'
      } else if (country.name === 'Australia') {
        return 'AUD'
      } else {
        return 'USD'
      }
    case '€':
      return 'EUR'
    case '£':
      return 'GBP'
    case 'CHF':
      return 'CHF'

    default:
      return 'USD'
  }
}

const setUrl = (label, value) => {
  if (value && value !== 'All') {
    urlRemove(label)
    urlAddExist(label, value)
  } else {
    urlRemove(label)
  }
}

const getUniqueCities = cities => {
  const filteredArray = cities.filter(item => item)

  const uniqueValuesSet = new Set(filteredArray)

  return Array.from(uniqueValuesSet)
}

const saveCustomLocations = async ({ campaignId, clientId }) => {
  if (campaignId && clientId) {
    const campaignLocations = await apiClient.getLocationByCampaignId(
      campaignId,
    )
    const campaignLocationsResponse = await campaignLocations.json()
    const newSelfServeLocations =
      campaignLocationsResponse && campaignLocationsResponse.length
        ? campaignLocationsResponse.filter(
            location => !!location.campaignId && !location.clientId,
          )
        : []
    if (newSelfServeLocations.length) {
      for (const newSelfServeLocation of newSelfServeLocations) {
        // update client_id in custom locations to show them as recommended custom location in future campaigns
        await apiClient.updateLocationById(newSelfServeLocation.id, {
          client_id: clientId,
        })
      }
    }
  }
}

export {
  filterPipelines,
  formatDate,
  formatExportData,
  openLinkInNewTab,
  openLinkInSameTab,
  onExportData,
  splitEmails,
  convertCityNameToValue,
  isHasCity,
  isHasSearch,
  getCampaignBuilderPageUrl,
  getDashboardPageUrl,
  getCodesPageUrl,
  getClientDashboardPageUrl,
  getDigitalProofPageUrl,
  encrypt,
  addAuthParamsToCampaignBuilderUrl,
  getCurrencyCode,
  setUrl,
  getUniqueCities,
  saveCustomLocations,
}
